import React, { useState, useEffect } from "react";
import Select from "react-select";
import { Form, Col, Container, Button, Spinner, Row } from "react-bootstrap";
import Alert from "../../common/Alert";
import SuperUserService from "../../../api-service/SuperUserService";
import StorageHelper from "../../../helpers/StorageHelper";
import SessionHelper from "../../../helpers/SessionHelper";
import MessageBanner from "../../common/MessageBanner";
import {
  ERROR_FETCHING_FARMER_LIST,
  ERROR_SENDING_MAIL,
  ERROR_WHILE_LOG_IN,
  INCOMING_TOKEN_HAS_EXPIRED,
  MAIL_SENT_WITH_FARMER_CREDENTIALS,
  PLEASE_SELECT_FARMER_TO_LOGIN,
  PLEASE_TRY_AGAIN_LATER,
  STAFF_AUTHORIZED,
  STAFF_FARMER_LOGIN_BANNER,
} from "../../../configurations/MessageConstants";
import {
  DANGER,
  EMPTY,
  INFO,
  LOGIN,
  SEND_MAIL,
  STAFF_SESSION_EXPIRED,
} from "../../../configurations/Constants";
import { StatusCodes } from "../../../configurations/status-codes";

function StaffAsFarmerLogin() {
  const [farmer, setFarmer] = useState(null);
  const [validated, setValidated] = useState(false);
  const [loginBtnClick, setLoginBtnClick] = useState(false);
  const [mailBtnClick, setMailBtnClick] = useState(false);

  const [alertMessage, setAlertMessage] = useState(EMPTY);
  const [variant, setVariant] = useState(EMPTY);
  const [showAlert, setShowAlert] = useState(false);
  const [farmersList, setFarmersList] = useState([]);

  useEffect(() => {
    (async function () {
      try {
        const { data } = await SuperUserService.getFarmerList();
        setFarmersList(data.data.users);
      } catch (error) {
        console.error(ERROR_FETCHING_FARMER_LIST, error);
      }
    })();
  }, []);

  const handleValidation = (event, action) => {
    const form = event.currentTarget;
    if (farmer && form.checkValidity() === true) {
      if (action === LOGIN) {
        event.preventDefault();
        HandleLogin();
      } else if (action === SEND_MAIL) {
        event.preventDefault();
        SendCredentialMail();
      }
    } else {
      event.preventDefault();
      event.stopPropagation();
    }
    setValidated(true);
  };

  const handleFarmerSelect = (value) => {
    setFarmer(value);
  };

  const SendCredentialMail = async () => {
    setMailBtnClick(true);
    try {
      await SuperUserService.SendCredentialMail(farmer.value);
      setMailBtnClick(false);
      setAlertMessage(MAIL_SENT_WITH_FARMER_CREDENTIALS);
      setVariant(INFO);
      setShowAlert(true);
    } catch (error) {
      console.error(ERROR_SENDING_MAIL, error);
      let responseData = error.response.data;
      if (
        error.response.status === StatusCodes.UNAUTHORIZED &&
        responseData.message.includes(INCOMING_TOKEN_HAS_EXPIRED)
      ) {
        StorageHelper.setItem(STAFF_SESSION_EXPIRED, true);
        SessionHelper.cleanUpStorageKeys();
        window.location.reload();
      } else if (error.response.status === StatusCodes.BAD_REQUEST) {
        setMailBtnClick(false);
        setAlertMessage(ERROR_SENDING_MAIL);
        setVariant(DANGER);
        setShowAlert(true);
      }
    }
  };

  const HandleLogin = async () => {
    setLoginBtnClick(true);
    try {
      const response = await SuperUserService.RequestToken(farmer.value);
      const url = "req?token=" + response.data.data.req_token;
      window.open(url, "_blank", "incognito=yes");

      setLoginBtnClick(false);
      setAlertMessage(EMPTY);
      setVariant(EMPTY);
      setShowAlert(false);
    } catch (error) {
      setLoginBtnClick(false);
      setAlertMessage(`${ERROR_WHILE_LOG_IN}. ${PLEASE_TRY_AGAIN_LATER}`);
      setVariant(DANGER);
      setShowAlert(true);
      console.error(ERROR_WHILE_LOG_IN, error);
    }
  };

  const handleCloseAlert = () => {
    setAlertMessage(EMPTY);
    setVariant(EMPTY);
    setShowAlert(false);
  };

  return (
    <div className="staff-as-farmer-login">
      <MessageBanner
        title={"Staff-Farmer Login"}
        description={`${STAFF_FARMER_LOGIN_BANNER} ${STAFF_AUTHORIZED}`}
      />
      <Container className="p-3 staff-as-farmer-login-container">
        <p className="text-muted">
          Enter the name or email to search for the farmer
        </p>
        {showAlert && (
          <Alert
            key="messageAlert"
            variant={variant}
            closeAlert={(e) => handleCloseAlert(e)}
            alertMessage={alertMessage}
            dismissible
          />
        )}
        <Form noValidate validated={validated}>
          <Row className="mb-3">
            <Form.Group as={Col} controlId="formFarmerSelect">
              <Select
                name="farmerData"
                options={farmersList}
                isClearable={true}
                className={`basic-multi-select ${
                  validated && !farmer
                    ? "staff-as-farmer-login-validation"
                    : validated && farmer
                    ? "staff-as-farmer-login-validation-success"
                    : EMPTY
                }`}
                onChange={handleFarmerSelect}
                value={farmer}
                placeholder="-- Select a farmer --"
              />
              <span className="staff-as-farmer-login-validation-message">
                {validated && !farmer ? PLEASE_SELECT_FARMER_TO_LOGIN : EMPTY}
              </span>
            </Form.Group>
          </Row>
          <Row className="d-flex flex-row justify-content-around staff-as-farmer-btns">
            <Button
              name="mailButton"
              variant="primary"
              type="button"
              className="h-50 staff-as-farmer-mailButton"
              disabled={loginBtnClick || mailBtnClick}
              onClick={(e) => handleValidation(e, SEND_MAIL)}
            >
              {mailBtnClick && (
                <Spinner
                  as="span"
                  animation="grow"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                />
              )}
              <span>
                Mail Credentials
                <i className="fa fa-envelope ms-2" aria-hidden="true"></i>
              </span>
            </Button>
            <Button
              name="loginButton"
              variant="primary"
              type="submit"
              className="h-50 ps-2 staff-as-farmer-loginButton"
              disabled={loginBtnClick || mailBtnClick}
              onClick={(e) => handleValidation(e, LOGIN)}
            >
              {loginBtnClick && (
                <Spinner
                  as="span"
                  animation="grow"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                />
              )}
              <span>
                Login As Farmer
                <i className="fa fa-user ms-2" aria-hidden="true"></i>
              </span>
            </Button>
          </Row>
        </Form>
      </Container>
    </div>
  );
}

export default StaffAsFarmerLogin;
